import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { InviteCreatorModal } from '../components/InviteCreatorModal';
import { 
    Send,
    Mail,
    Calendar,
    User,
    Clock,
    Loader2,
    CheckCircle,
    XCircle,
    AlertCircle,
    Trash2,
    Plus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function PosterInvitesPage() {
    const { token } = useAuth();
    const [invites, setInvites] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [cancelling, setCancelling] = useState(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [invitesRes, projectsRes] = await Promise.all([
                fetch(`${API}/poster/invites`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API}/poster/projects`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (invitesRes.ok) {
                const invitesData = await invitesRes.json();
                setInvites(invitesData);
            }
            if (projectsRes.ok) {
                const projectsData = await projectsRes.json();
                setProjects(projectsData);
            }
        } catch (error) {
            toast.error('Error loading data');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelInvite = async (inviteId) => {
        setCancelling(inviteId);
        try {
            const response = await fetch(`${API}/poster/invites/${inviteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Invite cancelled');
                fetchData();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to cancel invite');
            }
        } catch (error) {
            toast.error('Error cancelling invite');
        } finally {
            setCancelling(null);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'No date';
        return new Date(isoString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'sent':
                return <Clock className="w-4 h-4 text-blue-400" />;
            case 'accepted':
                return <CheckCircle className="w-4 h-4 text-green-400" />;
            case 'declined':
                return <XCircle className="w-4 h-4 text-red-400" />;
            case 'expired':
                return <AlertCircle className="w-4 h-4 text-gray-400" />;
            default:
                return null;
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
            declined: 'bg-red-500/20 text-red-400 border-red-500/30',
            expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
        };
        return styles[status] || styles.sent;
    };

    // Group invites by status
    const pendingInvites = invites.filter(inv => inv.status === 'sent');
    const acceptedInvites = invites.filter(inv => inv.status === 'accepted');
    const declinedInvites = invites.filter(inv => inv.status === 'declined');
    const expiredInvites = invites.filter(inv => inv.status === 'expired');

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-8">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">Invite to Project</h1>
                        <p className="text-[#A3A3A3]">
                            Invite artists and producers to submit to your projects
                        </p>
                    </div>
                    <Button
                        onClick={() => setShowInviteModal(true)}
                        className="bg-[#F51E1E] hover:bg-[#D91818] text-white"
                        disabled={projects.filter(p => p.status === 'open').length === 0}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Invite Creator
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                    </div>
                ) : invites.length === 0 ? (
                    <div className="text-center py-20 bg-[#1A1A1A] border border-[#333] rounded-xl">
                        <Send className="w-16 h-16 text-[#404040] mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">No Invites Sent</h2>
                        <p className="text-[#A3A3A3] mb-6">
                            Start inviting talented creators to submit to your projects.
                        </p>
                        <Button
                            onClick={() => setShowInviteModal(true)}
                            className="bg-[#F51E1E] hover:bg-[#D91818] text-white"
                            disabled={projects.filter(p => p.status === 'open').length === 0}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Invite Creator
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Stats Summary */}
                        <div className="grid grid-cols-4 gap-4">
                            <StatCard 
                                icon={<Clock className="w-5 h-5" />}
                                label="Pending"
                                count={pendingInvites.length}
                                color="blue"
                            />
                            <StatCard 
                                icon={<CheckCircle className="w-5 h-5" />}
                                label="Accepted"
                                count={acceptedInvites.length}
                                color="green"
                            />
                            <StatCard 
                                icon={<XCircle className="w-5 h-5" />}
                                label="Declined"
                                count={declinedInvites.length}
                                color="red"
                            />
                            <StatCard 
                                icon={<AlertCircle className="w-5 h-5" />}
                                label="Expired"
                                count={expiredInvites.length}
                                color="gray"
                            />
                        </div>

                        {/* Invites List */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl overflow-hidden">
                            <div className="px-6 py-4 border-b border-[#333]">
                                <h2 className="text-lg font-semibold text-white">All Invites</h2>
                            </div>
                            <div className="divide-y divide-[#333]">
                                {invites.map((invite, idx) => (
                                    <motion.div
                                        key={invite.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: idx * 0.02 }}
                                        className="px-6 py-4 hover:bg-[#262626] transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-[#262626] flex items-center justify-center">
                                                    {invite.submitter_user_id ? (
                                                        <User className="w-5 h-5 text-[#888]" />
                                                    ) : (
                                                        <Mail className="w-5 h-5 text-[#888]" />
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-white font-medium">
                                                            {invite.submitter_name || invite.email}
                                                        </span>
                                                        <Badge className={getStatusBadge(invite.status)}>
                                                            {invite.status}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-sm text-[#666]">
                                                        <span>{invite.project_title}</span>
                                                        <span>•</span>
                                                        <span>Sent {formatDate(invite.created_at)}</span>
                                                        {invite.deadline_at && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="flex items-center gap-1">
                                                                    <Calendar className="w-3 h-3" />
                                                                    Due {formatDate(invite.deadline_at)}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {invite.status === 'sent' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleCancelInvite(invite.id)}
                                                        disabled={cancelling === invite.id}
                                                        className="text-[#666] hover:text-red-400 hover:bg-red-400/10"
                                                    >
                                                        {cancelling === invite.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Invite Modal */}
                <InviteCreatorModal
                    open={showInviteModal}
                    onOpenChange={setShowInviteModal}
                    projects={projects}
                    onSuccess={() => fetchData()}
                />
            </div>
        </Layout>
    );
}

function StatCard({ icon, label, count, color }) {
    const colorClasses = {
        blue: 'text-blue-400 bg-blue-400/10',
        green: 'text-green-400 bg-green-400/10',
        red: 'text-red-400 bg-red-400/10',
        gray: 'text-gray-400 bg-gray-400/10'
    };

    return (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-4">
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                    {icon}
                </div>
                <div>
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-sm text-[#666]">{label}</p>
                </div>
            </div>
        </div>
    );
}
